/**
 * Экран 0 — лента акций с разбивкой по сферам и странам.
 *
 * Разрезы считает сервер и присылает вместе с лентой: клиент не знает, сколько
 * акций в сфере, которую он сейчас не показывает, а показывать вкладку, за
 * которой пусто, — тупик.
 */
import type { FeedSort } from '@allswipes/client';
import { Badge, Button, Card, Empty, Progress, Skeleton, num } from '@allswipes/ui';
import type { ReactNode } from 'react';
import { useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import { api } from '../api';
import { useT } from '@allswipes/i18n';
import { useReveal } from '../anim';
import { BrandRail } from '../components/BrandRail';
import { Subscribe } from '../components/Subscribe';
import { CampaignCard } from '../components/CampaignCard';
import { FeedFilters, type FeedQuery } from '../components/FeedFilters';
import { FeedFaq } from '../components/FeedFaq';
import { FeedHero } from '../components/FeedHero';
import { HeroRail } from '../components/HeroRail';
import { Shell } from '../components/Shell';
import { useResource } from '../hooks';
import { useSession } from '../session';

/** Значение страны в адресе, означающее «все страны». */
const ALL_REGIONS = 'all';

export function FeedScreen() {
  const t = useT();
  const { profile } = useSession();
  const [params, setParams] = useSearchParams();

  // По умолчанию лента показывает все страны, а не страну профиля: платформа
  // работает в десяти, и сузить её за человека значит спрятать от него девять
  // десятых. Страна выбирается вторым шагом и остаётся в адресе — ссылкой на
  // «Напитки в Узбекистане» можно поделиться.
  //
  // «Все страны» пишется в адрес отдельным словом, а не пустым значением:
  // пустое означало бы «не выбирал», и подстановка по умолчанию вернулась бы.
  const query: FeedQuery = useMemo(() => {
    const region = params.get('region');
    return {
      sphere: params.get('sphere') ?? '',
      region: !region || region === ALL_REGIONS ? '' : region,
      recipe: params.get('recipe') ?? '',
      sort: (params.get('sort') ?? '') as FeedSort,
      q: params.get('q') ?? '',
    };
  }, [params]);

  const setQuery = useCallback(
    (changes: Partial<FeedQuery>) => {
      const next = { ...query, ...changes };
      const search = new URLSearchParams();
      for (const [key, value] of Object.entries(next)) {
        if (key === 'region') continue;
        if (value) search.set(key, String(value));
      }
      search.set('region', next.region || ALL_REGIONS);
      setParams(search, { replace: true });
    },
    [query, setParams],
  );

  // Область человека в адрес не попадает: это не выбранный им фильтр, а
  // свойство профиля. Она сужает только те акции, которые сами объявили
  // штаты, — остальные идут по всей стране, как раньше.
  const subregion = profile?.user.subregion ?? '';

  const feed = useResource(
    () => api.feed({ ...query, subregion }),
    [query.sphere, query.region, query.recipe, query.sort, query.q, subregion],
  );

  // Карточки проявляются, когда сменился набор: выбрали сферу, страну,
  // поискали. На каждый рендер — нет: лента перерисовывается на каждое
  // нажатие клавиши в поиске, и мигать при этом ей незачем.
  const list = useRef<HTMLDivElement>(null);
  useReveal(list, [feed.data]);

  return (
    <Shell wide>
      {feed.loading && <Skeleton count={4} height={92} />}
      {feed.error && (
        <Card>
          <div className="h2">{t('Не загрузилось')}</div>
          <p className="sub" style={{ marginTop: 6 }}>{feed.error}</p>
          <div style={{ marginTop: 12 }}>
            <Button onClick={feed.reload}>{t('Повторить')}</Button>
          </div>
        </Card>
      )}

      {feed.data && (
        <div ref={list}>
          <FeedHero feed={feed.data} />

          <FeedFilters
            query={query}
            spheres={feed.data.spheres}
            regions={feed.data.regions}
            recipes={feed.data.recipes}
            found={feed.data.found}
            onChange={setQuery}
          />

          {profile && (
            <Card style={{ marginBottom: 16 }}>
              <div className="pts">
                <div>
                  <div className="lab">{t('Баллы')}</div>
                  <div className="val">{num(feed.data.points)}</div>
                </div>
                <div style={{ flex: 1 }}>
                  <div className="row" style={{ marginBottom: 6 }}>
                    <Badge tone="prize">{t(feed.data.rank.name)}</Badge>
                    <span className="spacer" />
                    {feed.data.next_rank && (
                      <span className="faint" style={{ fontSize: 11.5 }}>
                        {t('до «{rank}» — {left}', {
                          rank: t(feed.data.next_rank.name),
                          left: num(feed.data.next_rank.from - feed.data.points),
                        })}
                      </span>
                    )}
                  </div>
                  {feed.data.next_rank && (
                    <Progress
                      value={feed.data.points - feed.data.rank.from}
                      max={Math.max(1, feed.data.next_rank.from - feed.data.rank.from)}
                    />
                  )}
                </div>
              </div>
            </Card>
          )}

          <Section title={t('Мои участия')} show={feed.data.mine.length > 0}>
            <div className="ccards">
              {feed.data.mine.map((card) => (
                <CampaignCard key={card.campaign.id} card={card} />
              ))}
            </div>
          </Section>

          <Section title={t('Идут сейчас')} show={feed.data.active.length > 0}>
            <div className="ccards">
              {feed.data.active.map((card) => (
                <CampaignCard key={card.campaign.id} card={card} />
              ))}
            </div>
          </Section>

          <Section title={t('Скоро стартуют')} show={feed.data.soon.length > 0}>
            <div className="ccards">
              {feed.data.soon.map((card) => (
                <CampaignCard key={card.campaign.id} card={card} />
              ))}
            </div>
          </Section>

          <Section title={t('Итоги')} show={feed.data.finished.length > 0}>
            <div className="ccards">
              {feed.data.finished.map((card) => (
                <CampaignCard key={card.campaign.id} card={card} />
              ))}
            </div>
          </Section>

          <Section title={t('Бренды')} show={feed.data.brands.length > 0}>
            <BrandRail brands={feed.data.brands} />
          </Section>

          {/* Низ ленты собран по порядку вопросов, которые возникают у
              человека, долиставшего до конца: что важного происходит → а это
              вообще не обман? → ладно, зовите, когда появится новое. */}
          <HeroRail feed={feed.data} />

          <FeedFaq />

          <Subscribe />

          {feed.data.found === 0 &&
            (query.sphere || query.region || query.recipe || query.q ? (
              <Empty icon="search" title={t('Ничего не нашлось')}>
                {t('Попробуй другую сферу или страну — или сбрось фильтры и посмотри всё.')}
              </Empty>
            ) : (
              <Empty icon="flame" title={t('Пока нет активных акций')}>
                {t('Как только бренд запустит акцию в твоём регионе, она появится здесь.')}
              </Empty>
            ))}
        </div>
      )}
    </Shell>
  );
}

function Section({
  title,
  show,
  children,
}: {
  title: string;
  show: boolean;
  children: ReactNode;
}) {
  if (!show) return null;
  return (
    <>
      <div className="sec-head">
        <span className="lab">{title}</span>
      </div>
      {children}
    </>
  );
}
